/* MRC CRO Product V10 — isolated interactions. */
(() => {
  'use strict';

  const q = (root, selector) => root.querySelector(selector);
  const qa = (root, selector) => Array.from(root.querySelectorAll(selector));

  const init = (root) => {
    if (!root || root.dataset.mrcInitialized === 'true') return;
    root.dataset.mrcInitialized = 'true';

    // Defensive singleton guard. The CRO template intentionally renders each
    // major content zone once; remove later accidental duplicates if an app or
    // editor rehydrates the section more than once.
    const uniqueSeen = new Set();
    qa(root, '[data-mrc-unique]').forEach((element) => {
      const key = element.dataset.mrcUnique;
      if (!key) return;
      if (uniqueSeen.has(key)) element.remove();
      else uniqueSeen.add(key);
    });

    const cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
    const cartUrl = root.dataset.cartUrl || '/cart';
    const checkoutUrl = root.dataset.checkoutUrl || '/checkout';

    // Gallery
    const slides = qa(root, '[data-mrc-slide]');
    const thumbs = qa(root, '[data-mrc-thumb]');
    const counter = q(root, '[data-mrc-gallery-counter]');
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('active')));

    const showSlide = (index) => {
      if (!slides.length) return;
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('active', i === current));
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === current);
        thumb.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });
      if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
    };
    showSlide(current);
    q(root, '[data-mrc-gallery-prev]')?.addEventListener('click', () => showSlide(current - 1));
    q(root, '[data-mrc-gallery-next]')?.addEventListener('click', () => showSlide(current + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showSlide(Number(thumb.dataset.mrcThumb || 0))));

    // Quantity
    const qty = q(root, '[data-mrc-qty]');
    const clampQty = (next) => {
      if (!qty) return;
      const min = Number(qty.min || 1);
      const max = qty.max ? Number(qty.max) : Infinity;
      const step = Number(qty.step || 1);
      let val = Number(next);
      if (!Number.isFinite(val)) val = min;
      val = Math.max(min, Math.min(max, val));
      if (step > 1) val = min + Math.round((val - min) / step) * step;
      qty.value = String(val);
    };
    q(root, '[data-mrc-qty-minus]')?.addEventListener('click', () => clampQty(Number(qty?.value || 1) - Number(qty?.step || 1)));
    q(root, '[data-mrc-qty-plus]')?.addEventListener('click', () => clampQty(Number(qty?.value || 1) + Number(qty?.step || 1)));
    qty?.addEventListener('change', () => clampQty(qty.value));

    // Variant switching — all values come from Shopify-rendered data attributes.
    const variantInput = q(root, '[data-mrc-variant-input]');
    const variants = qa(root, '[data-mrc-variant]');
    const price = q(root, '[data-mrc-price]');
    const compare = q(root, '[data-mrc-compare]');
    const discount = q(root, '[data-mrc-discount]');
    const galleryBadge = q(root, '[data-mrc-gallery-badge]');
    const stock = q(root, '[data-mrc-stock]');
    const stockText = q(root, '[data-mrc-stock-text]');
    const addBtn = q(root, '[data-mrc-add-cart]');
    const addLabel = q(root, '[data-mrc-add-label]');
    const buyBtn = q(root, '[data-mrc-buy-now]');
    const buyPrice = q(root, '[data-mrc-buy-price]');
    const barPrice = q(root, '[data-mrc-bar-price]');
    const barTitle = q(root, '[data-mrc-bar-title]');
    const barAdd = q(root, '[data-mrc-bar-add]');

    const activateVariant = (button) => {
      variants.forEach((v) => {
        const active = v === button;
        v.classList.toggle('active', active);
        v.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      const id = button.dataset.variantId;
      const formatted = button.dataset.priceFormatted || '';
      const compareRaw = Number(button.dataset.compare || 0);
      const priceRaw = Number(button.dataset.price || 0);
      const compareFormatted = button.dataset.compareFormatted || '';
      const pct = Number(button.dataset.discount || 0);
      const available = button.dataset.available === 'true';
      const title = button.dataset.title || '';
      if (variantInput) variantInput.value = id;
      if (price) price.textContent = formatted;
      if (buyPrice) buyPrice.textContent = formatted;
      if (barPrice) barPrice.textContent = formatted;
      if (barTitle) barTitle.textContent = title;
      const onSale = compareRaw > priceRaw && pct > 0;
      if (compare) {
        compare.textContent = compareFormatted;
        compare.hidden = !onSale;
      }
      if (discount) {
        discount.textContent = `${pct}% OFF`;
        discount.hidden = !onSale;
      }
      if (galleryBadge) {
        galleryBadge.textContent = `${pct}% OFF`;
        galleryBadge.hidden = !onSale;
      }
      if (stock) stock.classList.toggle('is-out', !available);
      if (stockText) stockText.textContent = available ? 'In stock' : 'Sold out';
      [addBtn, buyBtn, barAdd].forEach((b) => { if (b) b.disabled = !available; });
      if (addLabel) addLabel.textContent = available ? 'Add to cart' : 'Sold out';
      if (barAdd) barAdd.textContent = available ? 'Add to cart' : 'Sold out';
      const mediaId = button.dataset.mediaId;
      if (mediaId) {
        const idx = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (idx >= 0) showSlide(idx);
      }
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}
    };
    variants.forEach((button) => button.addEventListener('click', () => activateVariant(button)));

    // Buy now: add selected variant, then use Shopify checkout.
    buyBtn?.addEventListener('click', async () => {
      if (!variantInput || buyBtn.disabled) return;
      buyBtn.disabled = true;
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantInput.value), quantity: Number(qty?.value || 1) })
        });
        if (!response.ok) throw new Error('Unable to add this item.');
        window.location.assign(checkoutUrl);
      } catch (error) {
        buyBtn.disabled = false;
        window.alert(error.message || 'Unable to continue to checkout.');
      }
    });

    // Sticky mobile bar uses the exact same theme add-to-cart button/form.
    barAdd?.addEventListener('click', () => {
      if (!barAdd.disabled && addBtn) addBtn.click();
    });

    // FAQ accordion
    qa(root, '[data-mrc-accordion] .acc__q').forEach((button) => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const panel = button.nextElementSibling;
        button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) panel.style.maxHeight = expanded ? '' : `${panel.scrollHeight}px`;
      });
    });

    // Internal CRO navigation should scroll without exposing dynamic Shopify section IDs in the URL.
    qa(root, 'a[href^="#mrc-"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const selector = link.getAttribute('href');
        if (!selector) return;
        let target = null;
        try { target = root.querySelector(selector) || document.querySelector(selector); } catch (_) {}
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          const clean = `${window.location.pathname}${window.location.search}`;
          window.history.replaceState(window.history.state, '', clean);
        } catch (_) {}
      });
    });
    try {
      if (window.location.hash && window.location.hash.startsWith('#mrc-')) {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
      }
    } catch (_) {}

    // Jump nav active state
    const jumpLinks = qa(root, '[data-mrc-jump] a');
    if ('IntersectionObserver' in window && jumpLinks.length) {
      const sections = jumpLinks.map((a) => {
        try { return root.querySelector(a.getAttribute('href')); } catch (_) { return null; }
      }).filter(Boolean);
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          jumpLinks.forEach((a) => a.classList.toggle('active', a.getAttribute('href') === `#${entry.target.id}`));
        });
      }, { rootMargin: '-40% 0px -50% 0px' });
      sections.forEach((section) => observer.observe(section));
    }

    // Related-products rail
    const rail = q(root, '[data-mrc-product-rail]');
    const railStep = () => (rail?.querySelector('.pcard')?.getBoundingClientRect().width || 280) + 16;
    q(root, '[data-mrc-rail-prev]')?.addEventListener('click', () => rail?.scrollBy({ left: -railStep() * 2, behavior: 'smooth' }));
    q(root, '[data-mrc-rail-next]')?.addEventListener('click', () => rail?.scrollBy({ left: railStep() * 2, behavior: 'smooth' }));

    // Frequently bought together
    const fbtItems = qa(root, '[data-mrc-fbt-item]');
    const fbtTotal = q(root, '[data-mrc-fbt-total]');
    const fbtAdd = q(root, '[data-mrc-fbt-add]');
    const formatINR = (cents) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(cents / 100);
    const updateFbt = () => {
      const total = fbtItems.filter((item) => item.checked).reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
      if (fbtTotal) fbtTotal.textContent = formatINR(total);
      if (fbtAdd) fbtAdd.disabled = total <= 0;
    };
    fbtItems.forEach((item) => item.addEventListener('change', updateFbt));
    updateFbt();
    fbtAdd?.addEventListener('click', async () => {
      const items = fbtItems.filter((item) => item.checked).map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
      if (!items.length) return;
      fbtAdd.disabled = true;
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Unable to add selected products.');
        window.location.assign(cartUrl);
      } catch (error) {
        fbtAdd.disabled = false;
        window.alert(error.message || 'Unable to add selected products.');
      }
    });


    // Judge.me review orchestration.
    // One hidden/native Review Widget remains the data source. We project it into:
    // 1) a photo-only 4–5 star rail near the top of the PDP, and
    // 2) the approved full review layout with 15 reviews initially + Load more.
    const reviewShell = q(root, '[data-mrc-product-reviews]');
    const photoSection = q(root, '[data-mrc-photo-review-section]');
    const photoRailHost = q(root, '[data-mrc-photo-review-rail]');
    let reviewBootstrapped = false;
    let reviewBusy = false;
    let currentReviewFilter = 'all';
    let visibleReviewCount = 15;
    const reviewCache = new Map();
    const reviewOrder = [];

    const getStars = (rating) => {
      const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
      return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
    };
    const getText = (node, selectors) => {
      for (const selector of selectors) {
        const el = node?.querySelector(selector);
        const value = el?.textContent?.replace(/\s+/g, ' ').trim();
        if (value) return value;
      }
      return '';
    };
    const reviewWidget = () => reviewShell?.querySelector('.jdgm-rev-widg');
    const sourceSignature = () => {
      const widget = reviewWidget();
      if (!widget) return '';
      const page = widget.querySelector('.jdgm-paginate__page.jdgm-curt')?.getAttribute('data-page') || '';
      const ids = qa(widget, '.jdgm-rev').map((review) => review.dataset.reviewId || review.textContent.slice(0, 60)).join('|');
      return `${page}:${ids}`;
    };
    const waitForSourceChange = (before, timeout = 3500) => new Promise((resolve) => {
      const widget = reviewWidget();
      if (!widget) return resolve(false);
      let done = false;
      const finish = (changed) => {
        if (done) return;
        done = true;
        observer.disconnect();
        clearInterval(timer);
        clearTimeout(limit);
        resolve(changed);
      };
      const check = () => {
        const now = sourceSignature();
        if (now && now !== before) finish(true);
      };
      const observer = new MutationObserver(check);
      observer.observe(widget, { childList: true, subtree: true, attributes: true });
      const timer = setInterval(check, 80);
      const limit = setTimeout(() => finish(false), timeout);
    });
    const clickAndWait = async (control) => {
      if (!control) return false;
      const before = sourceSignature();
      control.click();
      return waitForSourceChange(before);
    };
    const imageSource = (img) => img?.currentSrc || img?.getAttribute('data-src-retina') || img?.getAttribute('data-src') || img?.src || '';
    const extractReviewRating = (review) => {
      const scored = review.querySelector('[data-score]');
      let rating = Number(scored?.getAttribute('data-score') || 0);
      if (!rating) rating = review.querySelectorAll('.jdgm-rev__rating .jdgm-star.jdgm--on').length;
      return rating;
    };
    const reviewData = (review) => {
      const id = review.dataset.reviewId || `${getText(review, ['.jdgm-rev__author'])}-${getText(review, ['.jdgm-rev__timestamp'])}-${getText(review, ['.jdgm-rev__title'])}`;
      const pictures = qa(review, '.jdgm-rev__pics img').map(imageSource).filter(Boolean);
      const verifiedBadge = review.querySelector('.jdgm-rev__buyer-badge, .jdgm-rev__buyer-badge-wrapper');
      return {
        id,
        rating: extractReviewRating(review),
        verified: !!verifiedBadge && (verifiedBadge.textContent.trim().length > 0 || verifiedBadge.querySelector('.jdgm-rev__buyer-badge')),
        author: getText(review, ['.jdgm-rev__author', '.jdgm-rev__author-wrapper']) || 'Reviewer',
        date: getText(review, ['.jdgm-rev__timestamp']),
        title: getText(review, ['.jdgm-rev__title']),
        body: getText(review, ['.jdgm-rev__body']),
        pictures,
        productUrl: review.dataset.productUrl || '',
        productTitle: review.dataset.productTitle || ''
      };
    };
    const captureCurrentReviews = () => {
      const widget = reviewWidget();
      if (!widget) return [];
      const batch = qa(widget, '.jdgm-rev').map(reviewData);
      batch.forEach((data) => {
        if (!reviewCache.has(data.id)) reviewOrder.push(data.id);
        reviewCache.set(data.id, data);
      });
      return batch;
    };
    const currentPage = () => Number(reviewWidget()?.querySelector('.jdgm-paginate__page.jdgm-curt')?.getAttribute('data-page') || 1);
    const nextControl = () => reviewWidget()?.querySelector('.jdgm-paginate__next-page, .jdgm-paginate__page[rel="next"]');
    const loadNextNativePage = async (capture = true) => {
      const next = nextControl();
      if (!next || next.getAttribute('aria-disabled') === 'true' || next.classList.contains('jdgm-disabled')) return false;
      const beforePage = currentPage();
      const changed = await clickAndWait(next);
      if (!changed && currentPage() === beforePage) return false;
      if (capture) captureCurrentReviews();
      return currentPage() !== beforePage || changed;
    };
    const goToPageOne = async () => {
      const widget = reviewWidget();
      if (!widget || currentPage() === 1) return true;
      const first = widget.querySelector('.jdgm-paginate__page[data-page="1"]');
      if (!first) return false;
      await clickAndWait(first);
      return currentPage() === 1;
    };
    const changeSort = async (matcher) => {
      const widget = reviewWidget();
      const select = widget?.querySelector('.jdgm-sort-dropdown');
      if (!select) return null;
      const option = Array.from(select.options).find((item) => matcher(item.textContent || '', item.value || ''));
      if (!option) return null;
      const previous = select.value;
      if (select.value !== option.value) {
        const before = sourceSignature();
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        await waitForSourceChange(before);
      }
      return { select, previous };
    };
    const restoreSort = async (state) => {
      if (!state?.select || state.select.value === state.previous) return;
      const before = sourceSignature();
      state.select.value = state.previous;
      state.select.dispatchEvent(new Event('change', { bubbles: true }));
      await waitForSourceChange(before);
    };

    const renderPhotoRail = (photoReviews) => {
      if (!photoRailHost || !photoSection) return;
      photoRailHost.replaceChildren();
      if (!photoReviews.length) {
        photoSection.hidden = true;
        return;
      }
      photoSection.hidden = false;
      const viewport = document.createElement('div');
      viewport.className = 'mrc-store-review-viewport';
      const rail = document.createElement('div');
      rail.className = 'mrc-store-review-rail';
      viewport.appendChild(rail);
      photoReviews.slice(0, 10).forEach((data) => {
        const article = document.createElement('article');
        article.className = 'mrc-store-review-card';
        const media = document.createElement('div');
        media.className = 'mrc-store-review-media';
        const img = document.createElement('img');
        img.src = data.pictures[0];
        img.alt = `Customer photo from ${data.author}`;
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
        const body = document.createElement('div');
        body.className = 'mrc-store-review-body';
        const stars = document.createElement('div');
        stars.className = 'mrc-store-review-stars';
        stars.textContent = getStars(data.rating);
        body.appendChild(stars);
        if (data.title) {
          const title = document.createElement('h3');
          title.textContent = data.title;
          body.appendChild(title);
        }
        if (data.body) {
          const copy = document.createElement('p');
          copy.textContent = data.body;
          body.appendChild(copy);
        }
        const meta = document.createElement('div');
        meta.className = 'mrc-store-review-meta';
        if (data.verified) {
          const verified = document.createElement('span');
          verified.className = 'mrc-store-verified';
          verified.textContent = '✓ Verified';
          meta.appendChild(verified);
        }
        const reviewer = document.createElement('span');
        reviewer.className = 'mrc-store-reviewer';
        reviewer.textContent = data.author;
        meta.appendChild(reviewer);
        body.appendChild(meta);
        article.append(media, body);
        rail.appendChild(article);
      });
      photoRailHost.appendChild(viewport);
      if (photoReviews.length > 4) {
        const controls = document.createElement('div');
        controls.className = 'mrc-store-review-controls';
        const prev = document.createElement('button');
        const next = document.createElement('button');
        prev.type = next.type = 'button';
        prev.className = next.className = 'mrc-store-review-arrow';
        prev.setAttribute('aria-label', 'Previous photo reviews');
        next.setAttribute('aria-label', 'Next photo reviews');
        prev.textContent = '‹'; next.textContent = '›';
        const step = () => Math.max(280, viewport.clientWidth * .85);
        prev.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: 'smooth' }));
        next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: 'smooth' }));
        controls.append(prev, next);
        photoRailHost.appendChild(controls);
      }
    };

    const collectPhotoReviews = async () => {
      const widget = reviewWidget();
      if (!widget || !photoRailHost) return [];
      const photos = new Map();
      const collect = () => {
        qa(widget, '.jdgm-rev').map(reviewData).forEach((data) => {
          if (data.rating >= 4 && data.pictures.length) photos.set(data.id, data);
        });
      };
      // Ask Judge.me for image reviews directly where its native sorter is available.
      const sortState = await changeSort((label, value) => /only\s*pictures/i.test(label) || /only[-_ ]?pictures/i.test(value));
      collect();
      let guard = 0;
      // When Judge.me exposes Only Pictures, photo reviews are concentrated immediately.
      // Without that sorter, scan a few more native pages but stop early as soon as enough photos are found.
      const photoPageLimit = sortState ? 4 : 8;
      while (photos.size < 10 && guard < photoPageLimit) {
        guard += 1;
        const moved = await loadNextNativePage(false);
        if (!moved) break;
        collect();
      }
      renderPhotoRail(Array.from(photos.values()));
      await restoreSort(sortState);
      await goToPageOne();
      return Array.from(photos.values());
    };

    const filteredCachedReviews = () => reviewOrder.map((id) => reviewCache.get(id)).filter(Boolean).filter((data) => {
      if (currentReviewFilter === 'photos') return data.pictures.length > 0;
      if (currentReviewFilter === 'verified') return data.verified;
      if (currentReviewFilter === '5') return data.rating === 5;
      if (currentReviewFilter === '4') return data.rating === 4;
      if (currentReviewFilter === 'critical') return data.rating > 0 && data.rating <= 3;
      return true;
    });

    const buildReviewProjection = () => {
      if (!reviewShell) return;
      const widget = reviewWidget();
      if (!widget) return;
      const summary = widget.querySelector('.jdgm-rev-widg__summary');
      const histogramRows = qa(widget, '.jdgm-histogram__row[data-rating]');
      if (!summary && !reviewCache.size) return;
      let projection = reviewShell.querySelector('.mrc-review-projection');
      if (!projection) {
        projection = document.createElement('div');
        projection.className = 'mrc-review-projection';
        reviewShell.prepend(projection);
      }
      projection.replaceChildren();
      const scoreNode = summary?.querySelector('[data-score], .jdgm-rev-widg__summary-average');
      const scoreMatch = (summary?.textContent || '').match(/\d+(?:\.\d+)?/);
      const average = Number(widget.getAttribute('data-average-rating') || scoreNode?.getAttribute('data-score') || scoreNode?.textContent || scoreMatch?.[0] || 0);
      const countAttr = widget.getAttribute('data-number-of-reviews') || reviewShell.querySelector('[data-number-of-reviews]')?.getAttribute('data-number-of-reviews');
      const countMatch = (summary?.textContent || '').match(/(?:based on|from)\s+(\d+)/i);
      const reviewCount = Number(countAttr || countMatch?.[1] || reviewCache.size || 0);
      const summaryCard = document.createElement('div');
      summaryCard.className = 'mrc-review-summary-card';
      const scoreCol = document.createElement('div');
      scoreCol.className = 'mrc-review-score';
      scoreCol.innerHTML = `<div class="mrc-review-average">${average > 0 ? average.toFixed(1) : '0.0'}</div><div class="mrc-review-summary-stars">${getStars(average)}</div><small>Based on ${reviewCount} review${reviewCount === 1 ? '' : 's'}</small>`;
      const bars = document.createElement('div');
      bars.className = 'mrc-review-bars';
      if (histogramRows.length) {
        histogramRows.sort((a,b)=>Number(b.getAttribute('data-rating'))-Number(a.getAttribute('data-rating'))).forEach((row) => {
          const rating = Number(row.getAttribute('data-rating')) || 0;
          if (!rating) return;
          const frequencyText = getText(row, ['.jdgm-histogram__frequency']).replace(/[()]/g, '');
          const frequency = Number(frequencyText.replace(/[^0-9]/g, '')) || 0;
          const barContent = row.querySelector('.jdgm-histogram__bar-content');
          let width = barContent?.style?.width || '';
          if (!width && reviewCount) width = `${Math.min(100,(frequency/reviewCount)*100)}%`;
          const line = document.createElement('div');
          line.className = 'mrc-review-bar';
          line.innerHTML = `<span class="lab">${rating}★</span><span class="track"><span class="fill" style="width:${width || '0%'}"></span></span><span class="ct">${frequency}</span>`;
          bars.appendChild(line);
        });
      }
      summaryCard.append(scoreCol, bars);
      projection.appendChild(summaryCard);
      const toolbar = document.createElement('div');
      toolbar.className = 'mrc-review-toolbar';
      const filters = document.createElement('div');
      filters.className = 'mrc-review-filters';
      [['all','All'],['photos','With photos'],['verified','Verified only'],['5','5 star'],['4','4 star'],['critical','Critical']].forEach(([value,label]) => {
        const button = document.createElement('button');
        button.type='button';
        button.className=`mrc-review-filter${currentReviewFilter===value?' active':''}`;
        button.textContent=label;
        button.addEventListener('click',()=>{currentReviewFilter=value;buildReviewProjection();});
        filters.appendChild(button);
      });
      toolbar.appendChild(filters);
      const nativeSource = reviewShell.querySelector('[data-mrc-judgeme-native]') || reviewShell;
      const findNativeWriteTrigger = () => {
        const direct = nativeSource.querySelector('[data-testid="write-review-button"], .jdgm-write-rev-link, [data-write-review], [data-action="write-review"]');
        if (direct) return direct;
        return qa(nativeSource, 'button, a').find((element) => /write\s+a\s+review/i.test((element.textContent || '').replace(/\s+/g, ' ').trim())) || null;
      };
      const waitForNativeWriteTrigger = (timeout = 5000) => new Promise((resolve) => {
        const first = findNativeWriteTrigger();
        if (first) return resolve(first);
        const started = Date.now();
        let done = false;
        const finish = (value) => {
          if (done) return;
          done = true;
          observer.disconnect();
          clearInterval(timer);
          resolve(value);
        };
        const check = () => {
          const found = findNativeWriteTrigger();
          if (found) return finish(found);
          if (Date.now() - started >= timeout) finish(null);
        };
        const observer = new MutationObserver(check);
        observer.observe(nativeSource, { childList: true, subtree: true, attributes: true });
        const timer = setInterval(check, 100);
      });
      const write = document.createElement('button');
      write.type='button';
      write.className='mrc-review-write';
      write.textContent='Write a review';
      write.addEventListener('click', async () => {
        if (write.dataset.busy === 'true') return;
        write.dataset.busy = 'true';
        const originalLabel = write.textContent;
        write.textContent = 'Opening…';
        try {
          const trigger = findNativeWriteTrigger() || await waitForNativeWriteTrigger();
          if (!trigger) throw new Error('Judge.me review form is still loading.');
          trigger.click();
        } catch (error) {
          console.warn('[MRC CRO] Unable to open Judge.me review form:', error);
        } finally {
          window.setTimeout(() => {
            write.textContent = originalLabel;
            delete write.dataset.busy;
          }, 900);
        }
      });
      toolbar.appendChild(write);
      projection.appendChild(toolbar);
      const grid = document.createElement('div');
      grid.className='mrc-review-grid';
      const visible = filteredCachedReviews().slice(0, visibleReviewCount);
      visible.forEach((data) => {
        const card=document.createElement('article');
        card.className='mrc-review-card';
        const head=document.createElement('div');
        head.className='mrc-review-card-head';
        const avatar=document.createElement('span');
        avatar.className='mrc-review-avatar';
        avatar.textContent=(data.author.trim().charAt(0)||'R').toUpperCase();
        const author=document.createElement('span');
        author.className='mrc-review-author';
        const authorName=document.createElement('b');
        authorName.textContent=data.author;
        author.appendChild(authorName);
        if(data.verified){const v=document.createElement('span');v.className='mrc-review-verified';v.textContent='✓ Verified';author.appendChild(v);}
        const time=document.createElement('time');time.textContent=data.date;
        head.append(avatar,author,time);
        const stars=document.createElement('div');stars.className='mrc-review-card-stars';stars.textContent=getStars(data.rating);
        card.append(head,stars);
        if(data.title){const h=document.createElement('h3');h.textContent=data.title;card.appendChild(h);}
        if(data.body){const p=document.createElement('p');p.textContent=data.body;card.appendChild(p);}
        if(data.pictures.length){const media=document.createElement('div');media.className='mrc-review-media';data.pictures.forEach((src)=>{const img=document.createElement('img');img.src=src;img.loading='lazy';img.alt=`Review photo from ${data.author}`;media.appendChild(img);});card.appendChild(media);}
        grid.appendChild(card);
      });
      projection.appendChild(grid);
      const hasMoreCached = filteredCachedReviews().length > visibleReviewCount;
      const hasNativeNext = !!nextControl();
      if (hasMoreCached || hasNativeNext) {
        const load = document.createElement('button');
        load.type='button';
        load.className='mrc-review-load-more';
        load.textContent=reviewBusy?'Loading…':'Load more reviews';
        load.disabled=reviewBusy;
        load.addEventListener('click', async()=>{
          if(reviewBusy)return;
          reviewBusy=true;buildReviewProjection();
          if(filteredCachedReviews().length <= visibleReviewCount) await loadNextNativePage();
          visibleReviewCount += 5;
          reviewBusy=false;buildReviewProjection();
        });
        projection.appendChild(load);
      }
    };

    const bootstrapReviews = async () => {
      if (reviewBootstrapped || !reviewShell) return;
      const widget = reviewWidget();
      if (!widget || !qa(widget, '.jdgm-rev').length) return;
      reviewBootstrapped = true;
      reviewBusy = true;
      // First, harvest product photo reviews for the upper social-proof rail.
      await collectPhotoReviews();
      // Then start from page 1 and cache enough ordinary reviews for a meaningful first view.
      await goToPageOne();
      captureCurrentReviews();
      let guard=0;
      while(reviewCache.size < 15 && guard < 4){guard+=1;const moved=await loadNextNativePage();if(!moved)break;}
      visibleReviewCount=Math.min(15,Math.max(5,reviewCache.size));
      reviewBusy=false;
      buildReviewProjection();
    };

    const scheduleReviewBootstrap = () => window.setTimeout(bootstrapReviews, 80);
    scheduleReviewBootstrap();
    [300,800,1600,3000].forEach((delay)=>window.setTimeout(scheduleReviewBootstrap,delay));
  };

  const initAll = (scope = document) => {
    const roots = qa(scope, '.mrc-cro-page');
    if (scope === document) {
      const seenProducts = new Set();
      roots.forEach((root) => {
        const key = root.dataset.productId || root.id;
        if (seenProducts.has(key)) root.remove();
        else seenProducts.add(key);
      });
    }
    qa(scope, '.mrc-cro-page').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* ---- Kaching Bundles → MRC sticky bar price sync ---- */
(() => {
  'use strict';
  const SELECTED_PRICE = '.kaching-bundles__bar--selected .kaching-bundles__bar-price';
  const SELECTED_MULTI = '.kaching-bundles__bar--selected .kaching-bundles__bundle-products';
  const BAR_PRICE = '.mrc-cro-page [data-mrc-bar-price]';
  const BUY_NOW   = '.mrc-cro-page [data-mrc-buy-now]';

  const sync = () => {
    const src = document.querySelector(SELECTED_PRICE);
    const bar = document.querySelector(BAR_PRICE);
    if (src && bar) {
      const txt = (src.textContent || '').replace(/\s+/g, ' ').trim();
      if (txt && bar.textContent.trim() !== txt) bar.textContent = txt;
    }
    // Shopify's dynamic checkout can't carry a multi-product bundle — hide it on those deals.
    const buy = document.querySelector(BUY_NOW);
    if (buy) buy.style.display = document.querySelector(SELECTED_MULTI) ? 'none' : '';
  };

  let observer = null;
  const attach = () => {
    const root = document.querySelector('.kaching-bundles');
    if (!root) return false;
    observer = new MutationObserver(sync);
    observer.observe(root, { subtree: true, childList: true, characterData: true,
                             attributes: true, attributeFilter: ['class'] });
    sync();
    return true;
  };

  const boot = () => {
    if (attach()) return;
    let tries = 0;
    const timer = setInterval(() => { if (attach() || ++tries > 40) clearInterval(timer); }, 500);
  };

  // theme variant clicks rewrite the bar price — re-apply Kaching's total just after
  document.addEventListener('click', () => { setTimeout(sync, 60); setTimeout(sync, 350); }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();